"use client";

import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Hero, FeaturesSection, SEO } from "../components";
import { DealCard } from "@/components/deals";
import { ProductCard } from "@/components/ui/ProductCard";
import { DealGridSkeleton, HomeMenuSkeleton } from "@/components/skeletons";
import { useGetDealsQuery, useGetProductsQuery } from "@/store";

export default function HomeScreen() {
  const navigate = useNavigate();

  // Fetch featured deals from API
  const { data: dealsData, isLoading: dealsLoading } = useGetDealsQuery({
    featured: true,
    isActive: true,
    limit: 6,
  });
  const featuredDeals = dealsData?.data || [];

  // Fetch all products from API
  const { data: productsData, isLoading } = useGetProductsQuery();
  const allProducts = productsData?.data || [];
  const { data: outOfStockData } = useGetProductsQuery({
    inStock: false,
    limit: 3,
  });

  // Include a few unavailable products even when they fall beyond the API's
  // default first page, so customers can see their stock status on the home page.
  const availableProducts = allProducts.filter((product) => product.inStock);
  const unavailableProducts = outOfStockData?.data || [];
  const menuProducts = [
    ...availableProducts.slice(0, 12),
    ...unavailableProducts,
    ...availableProducts.slice(12, 15),
  ].slice(0, 15);

  return (
    <div>
      <SEO
        title="Delicious Food Delivery in Lagos | Nigerian & Continental Dishes"
        description="Experience fresh, delicious meals in Lagos. Order Nigerian food online with 24-hour delivery in Lagos. Best restaurant for rice, pasta, and local delicacies."
        canonical="/"
        organization={{
          name: "Zest and Zeal",
          url: "https://zestandzeal.com.ng",
        }}
      />
      <Hero />

      {/* Menu Section - Using OurMenuScreen layout */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              OUR MENU
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Food delivery on our finger lickin' meals made to
              perfection
            </p>
          </motion.div>

          {/* Menu Items Grid */}
          {isLoading ? (
            <HomeMenuSkeleton count={15} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
              {menuProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}

          {/* View More Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center"
          >
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/our-menu")}
              className="group"
            >
              View More
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Featured Deals */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 text-orange-500 mb-3">
              <Star className="h-6 w-6 fill-orange-500" />
              <span className="text-sm font-semibold tracking-widest">
                FEATURED
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Deals You&apos;ll Love
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Handpicked bundles and specials from our kitchen, available for a
              limited time.
            </p>
          </motion.div>

          {dealsLoading ? (
            <DealGridSkeleton count={3} />
          ) : featuredDeals.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              {featuredDeals.map((deal, index) => (
                <DealCard key={deal.id} deal={deal} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center text-muted-foreground">
              No featured deals available right now.
            </div>
          )}

          <div className="text-center">
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/deals")}
            >
              View All Deals
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <FeaturesSection />
    </div>
  );
}
